import { Button, HorizontalDivider, Image, Typography } from '@smartgift/frontend-toolkit';
import dayjs from 'dayjs';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  AUTOMATIONS_TAB,
  AVAILABLE_OCCASION_NAMES,
  RECOGNITIONS_TAB,
  dateLabelFormatWithTime
} from '@hero/constants';
import { OrderStatusBadge } from '@hero/molecules';
import { ROUTE_PATHS } from '@hero/routes';

import * as S from './style';

function OrderConfirmed({ id, data }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const isRecognition = window?.location.pathname.includes('recognition');

  return (
    <S.OrderConfirmed>
      <S.OrderBody>
        <S.OrderBodyHeader>
          <Typography variant="body1Bold">{t('status')}</Typography>
          <OrderStatusBadge status={data?.status?.current || data?.status} />
        </S.OrderBodyHeader>

        <HorizontalDivider />

        <S.OrderBodyContent>
          <Typography variant="body1Bold">
            {isRecognition ? t('orderSummary') : t('summary')}
          </Typography>
          <S.OrderBodyRow>
            <Typography variant="body2">
              {t(isRecognition ? 'giftCode' : 'automationCode')}:
            </Typography>
            <Typography variant="body2Bold">{data?.code}</Typography>
          </S.OrderBodyRow>

          {data?.settings?.expiresAt ? (
            <S.OrderBodyRow>
              <Typography variant="body2">{t('endDate')}:</Typography>
              <Typography variant="body2Bold">
                {dayjs(data?.settings?.expiresAt).format(dateLabelFormatWithTime)}
              </Typography>
            </S.OrderBodyRow>
          ) : (
            <S.OrderBodyRow>
              <Typography variant="body2">{t('startDate')}:</Typography>
              <Typography variant="body2Bold">
                {dayjs(data?.activatedAt).format(dateLabelFormatWithTime)}
              </Typography>
            </S.OrderBodyRow>
          )}

          {data?.type && !isRecognition && (
            <S.OrderBodyRow>
              <Typography variant="body2">{t('automationType')}:</Typography>
              <Typography variant="body2Bold">{t(AVAILABLE_OCCASION_NAMES[data.type])}</Typography>
            </S.OrderBodyRow>
          )}

          <S.OrderBodyRow>
            <Typography variant="body2">{t('name')}:</Typography>
            <Typography variant="body2Bold" style={{ wordBreak: 'break-word' }}>
              {data?.name}
            </Typography>
          </S.OrderBodyRow>
        </S.OrderBodyContent>

        <HorizontalDivider />

        <S.OrderBodyFooter>
          <S.OrderBodyFooterText>
            {
              <Trans
                i18nKey="orderConfirmedInfoText"
                components={{
                  a: (
                    <S.SupportLink
                      href={`mailto:${t('mailTo')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    />
                  )
                }}
              />
            }
          </S.OrderBodyFooterText>
          {isRecognition ? (
            <S.OrderBodyFooterActions>
              <Button
                variant="outlined"
                size="sm"
                onClick={() => {
                  navigate(ROUTE_PATHS.MY_RECOGNITION_DETAIL(id));
                }}
              >
                {t('seeDetails')}
              </Button>
              <Button
                variant="outlined"
                size="sm"
                onClick={() => {
                  navigate(
                    `${ROUTE_PATHS.MY_RECOGNITION_DETAIL(id)}#${RECOGNITIONS_TAB.analytics}`
                  );
                }}
              >
                {t('seeAnalytics')}
              </Button>
            </S.OrderBodyFooterActions>
          ) : (
            <S.OrderBodyFooterActions>
              <Button
                variant="outlined"
                size="sm"
                onClick={() => {
                  navigate(
                    `${ROUTE_PATHS.MY_AUTOMATION_DETAIL(id)}#${AUTOMATIONS_TAB.giftActivity}`
                  );
                }}
              >
                {t('seeGiftActivity')}
              </Button>
              <Button
                variant="outlined"
                size="sm"
                onClick={() => {
                  navigate(`${ROUTE_PATHS.MY_AUTOMATION_DETAIL(id)}#${AUTOMATIONS_TAB.overview}`);
                }}
              >
                {t('seeOverview')}
              </Button>
            </S.OrderBodyFooterActions>
          )}
        </S.OrderBodyFooter>
      </S.OrderBody>

      <S.OrderConfirmedMessage>
        <Image name="orderConfirmed" alt={t('orderConfirmed')} width={300} />
        {isRecognition ? (
          <>
            <Typography variant="body1Bold">{t('orderConfirmationTitle')}</Typography>
            <Typography variant="body2" color="fg" emphasis="weaker">
              <Trans
                i18nKey="orderConfirmationMessage"
                components={{
                  a1: (
                    <S.SupportLink
                      href={`${ROUTE_PATHS.DASHBOARD}${ROUTE_PATHS.MY_RECOGNITION_DETAIL(id)}#${
                        RECOGNITIONS_TAB.order
                      }`}
                    />
                  ),
                  a2: (
                    <S.SupportLink
                      href={`${ROUTE_PATHS.DASHBOARD}${ROUTE_PATHS.MY_RECOGNITION_DETAIL(id)}#${
                        RECOGNITIONS_TAB.analytics
                      }`}
                    />
                  )
                }}
              />
            </Typography>
          </>
        ) : (
          <>
            <Typography variant="body1Bold">{t('yourAutomationWasReceived')}</Typography>
            <Typography variant="body2" color="fg" emphasis="weaker">
              <Trans
                i18nKey="wantToSeeWhichRecipientsWillReceive"
                components={{
                  a1: (
                    <S.SupportLink
                      href={`${ROUTE_PATHS.DASHBOARD}${ROUTE_PATHS.MY_AUTOMATION_DETAIL(id)}#${
                        AUTOMATIONS_TAB.giftActivity
                      }`}
                    />
                  )
                }}
              />
            </Typography>
          </>
        )}
      </S.OrderConfirmedMessage>
    </S.OrderConfirmed>
  );
}

export { OrderConfirmed };
